<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import type { NavigationItem } from '~/types/navigation'
import type { Profile } from '~/types/profile'
import { useRouter } from '#imports'

const router = useRouter()
const isMenuOpen = ref(false)

defineProps({
	navigation: {
		type: Array<NavigationItem>,
		required: true,
	},
	desktop_navigation: {
		type: Array<NavigationItem>,
		required: true,
	},
	isAuthed: {
		type: Boolean,
		required: true,
	},
	profile: {
		type: Object as () => Profile | undefined,
		required: false,
	},
	submenu_logged_in: {
		type: Array<NavigationItem>,
		required: false,
		default: () => [],
	},
	submenu_logged_out: {
		type: Array<NavigationItem>,
		required: false,
		default: () => [],
	},
})

const emits = defineEmits(['toggleSearch'])

const toggleMenu = () => {
	isMenuOpen.value = !isMenuOpen.value
}

function menuClick(item: NavigationItem) {
	if (item.href) {
		const { $navigate } = useNuxtApp()
		$navigate(item.href)
		isMenuOpen.value = false
	}
}
</script>

<template>
	<div class="min-h-screen bg-secondary">
		<!-- Draw menu -->
		<TransitionRoot as="template" :show="isMenuOpen">
			<Dialog as="div" class="relative z-50" @close="isMenuOpen = false">
				<TransitionChild
					as="template"
					enter="transition-opacity ease-linear duration-300"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="transition-opacity ease-linear duration-300"
					leave-from="opacity-100"
					leave-to="opacity-0">
					<div class="fixed inset-0 bg-slate-900 bg-opacity-25" />
				</TransitionChild>

				<div class="fixed inset-0 z-50 flex">
					<TransitionChild
						as="template"
						enter="transition ease-in-out duration-300 transform"
						enter-from="-translate-x-full"
						enter-to="translate-x-0"
						leave="transition ease-in-out duration-300 transform"
						leave-from="translate-x-0"
						leave-to="-translate-x-full">
						<DialogPanel class="relative flex w-full max-w-xs flex-1 flex-col bg-white pb-4 pt-5">
							<div>
								<nav class="space-y-1">
									<div v-for="item in navigation" :key="item.name">

										<span
											v-if="item.comingSoon"
											:class="[
												'block p-4 menu-item text-base font-medium text-gray-400 no-underline',
												item.hideMobile ? 'hidden lg:block' : '',
											]">
											<Icon :name="item.icon || ''" class="pl-4 mr-4 h-6 w-6 icon-fix" />
											<span class="px-2">{{ item.name }}</span>
										</span>

										<RouterLink
											v-else
											@click="menuClick(item)"
											to="#"
											:class="[
												item.current
													? 'border-green-500 bg-green-50 text-green-700'
													: 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
												'block border-l-4 p-4 text-base font-medium cursor-pointer no-underline !outline-none',
												item.hideMobile ? 'hidden lg:block' : '',
											]">
											<Icon :name="item.icon || ''" class="pl-4 mr-4 h-6 w-6 icon-fix" />
											<span class="px-2">{{ item.name }}</span>
										</RouterLink>
									</div>
								</nav>

								<div class="border-t border-gray-200 pb-3 pt-4">
									<div v-if="isAuthed && profile?.id">
										<div v-for="item in submenu_logged_in" :key="item.name">
											<RouterLink
												@click="menuClick(item)"
												to="#"
												:class="[
													item.current
														? 'border-green-500 bg-green-50 text-green-700'
														: 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
													'block border-l-4 p-4 text-base font-medium cursor-pointer no-underline',
												]">
												<Icon :name="item.icon as string" class="pl-4 mr-4 h-6 w-6 icon-fix" />
												<span class="px-2 no-underline">{{ item.name }}</span>
											</RouterLink>
										</div>
									</div>
									<div v-else>
										<div v-for="item in submenu_logged_out" :key="item.name">
											<RouterLink
												@click="menuClick(item)"
												to="#"
												:class="[
													item.current
														? 'bg-green-50 text-green-700 border-green-500'
														: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent',
													'block border-l-4 p-4 text-base font-medium cursor-pointer no-underline',
												]">
												<Icon :name="item.icon as string" class="pl-4 mr-4 h-6 w-6 icon-fix" />
												<span class="px-2 no-underline">{{ item.name }}</span>
											</RouterLink>
										</div>
									</div>
								</div>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</Dialog>
		</TransitionRoot>

		<!-- Mobile header -->
		<div
			class="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 bg-white px-4 sm:gap-x-6 sm:px-6 lg:hidden">
			<button type="button" class="mt-2 p-2.5 text-gray-700 lg:hidden" @click="toggleMenu">
				<span class="sr-only">Open sidebar</span>
				<Icon name="ph:list" class="h-6 w-6 text-black" aria-hidden="true" />
			</button>

			<div class="flex flex-1 items-center justify-between">
				<img
					class="desktop-logo cursor-pointer ml-1"
					src="~/public/logo-short.png"
					alt="Health Nutrition"
					@click="toggleMenu" />
				<div class="flex items-center gap-x-4">
					<Icon name="ph:magnifying-glass"
						class="size-6 mr-2 cursor-pointer"
						@click="emits('toggleSearch')"
						/>
					<Icon v-if="profile" name="ph:bell-ringing-duotone" class="h-6 w-6 shake bell" aria-hidden="true" />
					<Avatar
						v-if="profile?.id"
						hide-popover
						:profile="profile"
						:self="profile"
						:is-authed="isAuthed"
						:size="8"
						type="rounded"
						to="/profile"
						class="shrink-0" />
				</div>
			</div>
		</div>

		<!-- Desktop header -->
		<div class="hidden lg:flex lg:w-full lg:flex-col">
			<div class="flex h-16 shrink-0 items-center justify-between bg-white px-6 lg:px-8">
				<div class="flex mx-auto items-center justify-between w-full max-w-7xl">
					<div class="flex items-center gap-x-4">
					<img
						class="desktop-logo cursor-pointer"
						src="~/public/logo-short.png"
						alt="Health Nutrition"
						@click="toggleMenu" />
					<nav class="main-nav ml-12 gap-8">
						<div v-for="item in desktop_navigation" :key="item.name" class="main-nav-item flex flex-col">
							<span v-if="item.comingSoon" class="text-gray-400">{{ item.name }}</span>
							<RouterLink v-else :to="item.href" :class="[item.current ? 'router-link-active' : '']">{{
								item.name
							}}</RouterLink>
						</div>
						<button
							type="button"
							class="main-nav-item cursor-pointer hover:text-green-400"
							@click="toggleMenu">
							More...
						</button>
					</nav>
				</div>

				<div class="flex items-center gap-x-4">
					<Icon
								name="ph:magnifying-glass"
								class="size-6 mr-2 cursor-pointer"
								@click="emits('toggleSearch')"
							/>

					<div v-if="isAuthed && profile?.id" class="flex items-center gap-x-4">

						<Icon
							v-if="profile"
							name="ph:bell-ringing-duotone"
							class="h-6 w-6 shake bell"
							aria-hidden="true" />
						<Avatar
							v-if="profile?.id"
							hide-popover
							:profile="profile"
							:self="profile"
							:is-authed="isAuthed"
							:size="8"
							type="rounded"
							to="/profile"
							class="shrink-0" />
					</div>
					<div v-else>
						<RouterLink :to="`/login?redirect=${router.currentRoute.value.fullPath}`" class="btn-primary mr-2">Login</RouterLink>
					</div>
				</div>
				</div>

			</div>
		</div>

		<main>
				<slot />
		</main>
	</div>
</template>

<style scoped>
.bell:hover {
	animation: shake 1s infinite;
}

.bell:focus {
	animation: shake 1s infinite;
}

@keyframes shake {
	0% {
		transform: rotate(0deg);
	}

	25% {
		transform: rotate(10deg);
	}

	50% {
		transform: rotate(0eg);
	}

	75% {
		transform: rotate(-10deg);
	}

	100% {
		transform: rotate(0deg);
	}
}
</style>
